import { memo } from '@tanstack/react-table';

import { defaultComparators } from './default-comparators.js';
import {
  normalizeThresholdRules,
  type DataTableV2ThresholdsNormalizedRule,
} from './normalize-rules.js';
import {
  type DataTableV2ColumnThreshold,
  type DataTableV2RowThreshold,
} from './threshold-types.js';
import { useDataTableV2ConfigElement } from '../../../hooks/useDataTableV2Config.js';
import {
  isAccessorFnColumnDef,
  isAccessorKeyColumnDef,
  isGroupColumnDef,
} from '../../hooks/useTable/datatablev2-typeguards.js';
import {
  type Cell,
  type Column,
  type Row,
  type RowData,
  type Table,
  type TableFeature,
  type TableOptions,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * Extension interface to extend the tanstack table with row level thresholds .
 * @public
 */
export interface DataTableV2ThresholdProps<TData extends DataTableV2RowData> {
  /**
   * Configures the thresholds used for the rows highlighting.
   */
  rowThresholds?: DataTableV2RowThreshold<TData>[];
}

/**
 * Extension interface to extend the types from the tanstack table options to support row level thresholds.
 * @internal
 */
export interface DataTableV2RowThresholdOptions<
  TData extends DataTableV2RowData,
> {
  /**
   * Defines if there are thresholds configured on the tables instance.
   */
  hasThresholds?: boolean;
  /**
   * Normalized row threshold options.
   */
  rowThresholds?: DataTableV2ThresholdsNormalizedRule<TData>[];
}

/**
 * Extension interface to extend the types of the columnDefinition from the tanstack table
 * to support column level thresholds.
 * @public
 */
export interface DataTableV2ThresholdColumnDef<
  TData extends DataTableV2RowData,
> {
  /**
   * Configures the thresholds used for the cell highlighting.
   */
  thresholds?: DataTableV2ColumnThreshold<TData>[];
}

/**
 * Extension interface to extend the types of the `Row` from the tanstack table.
 * @internal
 */
export interface DataTableV2ThresholdRow {
  /**
   * @internal
   * Get the threshold colors for the row.
   */
  getThresholdColors: () => {
    color?: string;
    backgroundColor?: string;
  } | null;

  /**
   * @internal
   * Get the pill color for the row threshold.
   */
  getThresholdPill: () => string | null;
}

/**
 * Extension interface to extend the types of the `Column` from the tanstack table
 * @internal
 */
export interface DataTableV2ThresholdColumn<TData extends DataTableV2RowData> {
  /**
   * @internal
   * Returns the normalized threshold rules on a column level.
   */
  _getThresholdRules: () => Array<
    DataTableV2ThresholdsNormalizedRule<TData>
  > | null;
}

/**
 * @internal
 * Extension interface to extend the types of the `Cell` from the tanstack table.
 */
export interface DataTableV2ThresholdCell {
  /**
   * @internal
   * Get the threshold color and backgroundColor for the cell.
   */
  getThresholdColors: () => { color?: string; backgroundColor?: string };
}

/**
 * @internal
 * Hook configuring the thresholds feature for the DataTableV2.
 *
 */
export function useThresholds<TData>(
  props: DataTableV2ThresholdProps<TData>,
  options: TableOptions<TData>,
) {
  const configThresholds = useDataTableV2ConfigElement('rowThresholds');

  // check if either the table or any columns have thresholds
  const rowThresholds = props.rowThresholds ?? configThresholds;

  // First check if rowThresholds is defined, because this is the cheaper check
  if (rowThresholds) {
    options.hasThresholds = true;
  } else {
    // Check through the columnDefs if there are thresholds defined.
    options.hasThresholds = options.columns.some((column) => {
      if (isGroupColumnDef(column)) {
        const groupedColumn = column;
        return (
          groupedColumn.columns &&
          groupedColumn.columns.some(
            (childColumn) =>
              childColumn.thresholds && childColumn.thresholds.length > 0,
          )
        );
      } else {
        return column.thresholds && column.thresholds.length > 0;
      }
    });
  }

  if (rowThresholds) {
    options.rowThresholds = normalizeThresholdRules(rowThresholds);
  }
  // Make sure to reset it back if the rowThreshold prop is reset.
  else if (rowThresholds === undefined) {
    options.rowThresholds = undefined;
  }
}

/**
 * @internal
 * Helper function that will check if all rules of a given threshold match.
 */
function matchesThreshold<TData extends DataTableV2RowData>(
  threshold: DataTableV2ThresholdsNormalizedRule<TData>,
  rowData: TData,
): boolean {
  return threshold.rules.every((rule) => {
    const compareValue = rule.accessor?.(rowData) ?? undefined;
    if (typeof rule.comparator === 'string') {
      return defaultComparators[rule.comparator](rule.value, compareValue);
    }
    return rule.comparator(rowData);
  });
}

/**
 * Feature to handle thresholds to help in row and cell highlighting.
 * @internal
 */
export const Thresholds: TableFeature = {
  createRow<TData extends DataTableV2RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ) {
    /**
     * @internal
     * Get the pill color for the row threshold.
     */
    row.getThresholdPill = memo(
      () => [table.options.rowThresholds, row, row.index],
      () => {
        const thresholds = table.options.rowThresholds;
        if (thresholds === undefined || thresholds.length === 0) {
          return null;
        }

        // Check from the back, because the last one matching one should be
        // the one applying, if there are more than one rules applied.
        for (let i = thresholds?.length - 1; i >= 0; i--) {
          const currentThreshold = thresholds[i];
          // Only consider 'pill' typed thresholds.
          if (currentThreshold.type !== 'pill') {
            continue;
          }

          const matchesRule = matchesThreshold(currentThreshold, row.original);
          if (matchesRule && currentThreshold.color) {
            return currentThreshold.color;
          }
        }

        return null;
      },
      getMemoOptions(table.options, 'debugRows', 'table.getThresholdPill'),
    );

    /**
     * @internal
     * Get the threshold colors for the row.
     */
    row.getThresholdColors = memo(
      () => [table.options.rowThresholds, row, row.index],
      () => {
        const thresholds = table.options.rowThresholds;
        if (thresholds === undefined || thresholds.length === 0) {
          return null;
        }

        // Check from the back, because the last one matching one should be
        // the one applying, if there are more than one rules applied.
        for (let i = thresholds?.length - 1; i >= 0; i--) {
          const currentThreshold = thresholds[i];
          // Only consider 'highlight' typed thresholds.
          if (currentThreshold.type !== 'highlight') {
            continue;
          }

          const matchesRule = matchesThreshold(currentThreshold, row.original);
          if (matchesRule === true) {
            return {
              color: currentThreshold.color,
              backgroundColor: currentThreshold.backgroundColor,
            };
          }
        }

        return null;
      },
      getMemoOptions(table.options, 'debugRows', 'table.getThresholdColors'),
    );
  },

  createColumn<TData extends DataTableV2RowData>(
    column: Column<TData>,
    table: Table<TData>,
  ) {
    /**
     * @internal
     * Returns the normalized threshold rules on a column level.
     */
    column._getThresholdRules = memo(
      () => [column.columnDef.thresholds],
      () => {
        if (
          column.columnDef.thresholds === undefined ||
          column.columnDef.thresholds.length === 0
        ) {
          return null;
        }

        // Calculate the fallback accessor that should be used if there is
        // non given on the threshold rule.
        let fallbackAccessor: string | ((row: TData) => unknown) | undefined;
        if (isAccessorKeyColumnDef(column.columnDef)) {
          fallbackAccessor = column.columnDef.accessorKey;
        }
        if (isAccessorFnColumnDef(column.columnDef)) {
          const accessorFn = column.columnDef.accessorFn;
          fallbackAccessor = (row: TData) => accessorFn(row, 0);
        }

        return normalizeThresholdRules(
          column.columnDef.thresholds,
          fallbackAccessor,
        );
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column._getThresholdRules',
      ),
    );
  },

  createCell<TData extends RowData, TValue>(
    cell: Cell<TData, TValue>,
    column: Column<TData, TValue>,
    row: Row<TData>,
    table: Table<TData>,
  ) {
    /**
     * @internal
     * Get the threshold color and backgroundColor for the cell.
     */
    cell.getThresholdColors = () => {
      const thresholds = column._getThresholdRules();

      const matchedColors: { color?: string; backgroundColor?: string } = {
        color: undefined,
        backgroundColor: undefined,
      };

      if (thresholds) {
        // Check from the back, because the last one matching one should be
        // the one applying, if there are more than one rules applied.
        for (let i = thresholds?.length - 1; i >= 0; i--) {
          const currentThreshold = thresholds[i];
          const matchesRule = matchesThreshold(currentThreshold, row.original);

          if (matchesRule) {
            matchedColors.color = currentThreshold.color;
            matchedColors.backgroundColor = currentThreshold.backgroundColor;
            return matchedColors;
          }
        }
      }

      // If color and backgroundColor are already defined, there is no need to check on the row.
      if (
        matchedColors.color === undefined ||
        matchedColors.backgroundColor === undefined
      ) {
        // If no column threshold rules matched, we can check if the row threshold
        // rule will match.
        const rowThresholds = row.getThresholdColors();
        if (rowThresholds) {
          matchedColors.color = matchedColors.color ?? rowThresholds.color;
          matchedColors.backgroundColor =
            matchedColors.backgroundColor ?? rowThresholds.backgroundColor;
        }
      }

      return matchedColors;
    };
  },
};
